import React from 'react';
import type { MenuProps } from 'antd';
import { Layout, Menu, Avatar, Row, Button, Drawer } from 'antd';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { Logo } from './logo';
import { MenuSections } from '../../types/menu-sections';
import { tokens } from '../../theme/tokens';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';

const { Header } = Layout;
type MenuItem = Required<MenuProps>['items'][number];

interface UserProfile {
  'custom:clientId'?: string;
  email?: string;
}

interface AppHeaderProps {
  activeKey: MenuSections;
  items: MenuItem[];
  userInfo?: UserProfile;
  onKeySelect: (key: MenuSections) => void;
  onSignOut: () => void;
  drawerExtraContent?: React.ReactNode;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  activeKey,
  items,
  userInfo,
  onKeySelect,
  onSignOut,
  drawerExtraContent
}) => {
  const { fontSize, screens, spacing } = useResponsiveSizes();
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  return (
    <>
      <Header
        style={{
          background: tokens.colors.primary.neutral[1000],
          borderBottom: 'none',
          padding: screens.xs ? '0 16px' : '0 50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 64,
          position: 'relative',
          fontSize: fontSize.base
        }}
      >
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Logo />
        </div>

        {/* Desktop: Menu + Perfil */}
        {screens.md ? (
          <>
            <Menu
              mode='horizontal'
              selectedKeys={[activeKey]}
              onClick={({ key }) => onKeySelect(key as MenuSections)}
              items={items}
              className='custom-menu'
              style={{
                flex: 1,
                justifyContent: 'center',
                borderBottom: 'none',
                gap: '10px',
                fontWeight: '600',
                background: tokens.colors.primary.neutral[1000],
                fontSize: fontSize.base
              }}
            />
            <Row align='middle' style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Avatar size={30} style={{ pointerEvents: 'none' }}>
                {userInfo?.['custom:clientId']?.[0]?.toUpperCase() || '?'}
              </Avatar>
              {screens.lg && (
                <Button
                  type='text'
                  onClick={onSignOut}
                  style={{ color: tokens.colors.primary.neutral[50], fontWeight: '600', fontSize: fontSize.base }}
                >
                  Salir
                </Button>
              )}
            </Row>
          </>
        ) : (
          /* Móvil: Hamburger */
          <Button
            type='text'
            icon={<MenuOutlined style={{ fontSize: 20, color: tokens.colors.primary.neutral[50] }} />}
            onClick={() => setDrawerOpen(true)}
          />
        )}

        {/* Línea de gradiente */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: `linear-gradient(90deg, ${tokens.colors.brands.blue[600]} 0%, ${tokens.colors.brands.magenta[800]} 100%)`
          }}
        />
      </Header>

      {/* Drawer para móviles */}
      <Drawer
        placement='right'
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={280}
        closeIcon={<CloseOutlined style={{ color: tokens.colors.primary.neutral[50], fontSize: 16 }} />}
        styles={{
          body: { padding: 0, background: tokens.colors.primary.neutral[1000] },
          header: { background: tokens.colors.primary.neutral[1000], borderBottom: 'none' }
        }}
      >
        {/* Perfil en drawer */}
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}
        >
          <Avatar size={40}>{userInfo?.['custom:clientId']?.[0]?.toUpperCase() || '?'}</Avatar>
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
            <div style={{ color: tokens.colors.primary.neutral[50], fontWeight: 600 }}>
              {userInfo?.['custom:clientId']}
            </div>
            <div style={{ color: tokens.colors.primary.neutral[400], fontSize: 12 }}>{userInfo?.email}</div>
          </div>
        </div>

        {/* Botón salir con colores custom */}
        <div
          style={{
            padding: '16px 24px 24px 24px',
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          <Button
            onClick={() => {
              onSignOut();
              setDrawerOpen(false);
            }}
            style={{
              fontSize: fontSize.base,
              background: 'rgba(255,255,255,0.1)',
              color: tokens.colors.primary.neutral[50],
              border: `1px solid rgba(255,255,255,0.2)`,
              fontWeight: 600,
              width: '100%'
            }}
          >
            Salir
          </Button>
        </div>

        {/* Menu items con tema oscuro */}
        <Menu
          mode='vertical'
          selectedKeys={[activeKey]}
          onClick={({ key }) => {
            onKeySelect(key as MenuSections);
            setDrawerOpen(false);
          }}
          items={items}
          theme='dark'
          style={{
            background: tokens.colors.primary.neutral[1000],
            border: 'none',
            fontSize: fontSize.lg
          }}
        />

        {/* Contenido adicional (TreeView en Library) */}
        {drawerExtraContent && (
          <>
            <div
              style={{
                height: '1px',
                background: 'rgba(255,255,255,0.1)',
                margin: '16px 0'
              }}
            />
            <div
              style={{
                padding: '0 16px',
                maxHeight: 'calc(100vh - 450px)',
                overflowY: 'auto',
                paddingBottom: 80
              }}
            >
              <div
                style={{
                  color: tokens.colors.primary.neutral[400],
                  fontSize: fontSize.xs,
                  fontWeight: 600,
                  letterSpacing: '1px',
                  marginBottom: 12,
                  textTransform: 'uppercase'
                }}
              >
                Carpetas
              </div>
              {drawerExtraContent}
            </div>
          </>
        )}
      </Drawer>
    </>
  );
};
